import { isInvoiceListReturnContext } from "@/accounts-payable/common/invoice-return-context";
import type {
  ApReviewQueueItem,
  ApReviewQueueReturnPosition,
} from "@/accounts-payable/types";
import { dayjs } from "@/core/utils/date";

const isQueueReturnPosition = (
  value: unknown,
): value is ApReviewQueueReturnPosition => {
  if (!value || typeof value !== "object") return false;
  const position = value as Record<string, unknown>;
  const { invoiceIds, invoiceIndex } = position;
  return (
    isInvoiceListReturnContext(position) &&
    Array.isArray(invoiceIds) &&
    invoiceIds.every((id: unknown) => typeof id === "string") &&
    typeof invoiceIndex === "number" &&
    Number.isInteger(invoiceIndex) &&
    invoiceIndex >= 0 &&
    invoiceIndex < invoiceIds.length &&
    typeof position.invoiceCreatedAt === "string" &&
    typeof position.offset === "number" &&
    Number.isFinite(position.offset) &&
    typeof position.scrollTop === "number" &&
    Number.isFinite(position.scrollTop) &&
    typeof position.pageCount === "number" &&
    Number.isInteger(position.pageCount) &&
    position.pageCount >= 1
  );
};

/** Validate history state and keep its stable identity across renders. */
export const readQueueReturnPosition = (
  state: unknown,
  businessId: string | null,
): ApReviewQueueReturnPosition | null => {
  if (!businessId || !state || typeof state !== "object") return null;
  if (!("queueReturnPosition" in state)) return null;
  const position = state.queueReturnPosition;
  return isQueueReturnPosition(position) && position.businessId === businessId
    ? position
    : null;
};

/** Restore saved depth and search past new arrivals in the server's date order. */
export const needsMoreQueuePages = ({
  position,
  items,
  loadedPageCount,
  hasNextPage,
}: {
  position: ApReviewQueueReturnPosition;
  items: Pick<ApReviewQueueItem, "id" | "created_at">[];
  loadedPageCount: number;
  hasNextPage: boolean;
}) => {
  if (!hasNextPage) return false;
  if (loadedPageCount < position.pageCount) return true;
  const invoiceId = position.invoiceIds[position.invoiceIndex];
  if (items.some((item) => item.id === invoiceId)) return false;
  const lastItem = items[items.length - 1];
  return (
    !!lastItem &&
    !dayjs(lastItem.created_at).isBefore(dayjs(position.invoiceCreatedAt))
  );
};

/** Prefer the next surviving invoice, then the previous one at each distance. */
export const getQueueReturnIndex = (
  position: ApReviewQueueReturnPosition,
  currentIds: string[],
) => {
  const indices = new Map(currentIds.map((id, index) => [id, index]));
  for (let distance = 0; distance < position.invoiceIds.length; distance++) {
    for (const index of [
      position.invoiceIndex + distance,
      position.invoiceIndex - distance,
    ]) {
      const currentIndex = indices.get(position.invoiceIds[index]);
      if (currentIndex !== undefined) return currentIndex;
    }
  }
  return Math.min(position.invoiceIndex, currentIds.length - 1);
};
